/**
 * TokensBadge — small chip in the top bar showing the user's fierro balance
 * (or "ilimitado" if they're on an active subscription). Clicking it navigates
 * to the plans/billing page so users can top up in one click.
 */

import { Link } from 'react-router-dom';
import { Infinity as InfinityIcon } from 'lucide-react';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { useAuthStore } from '@/stores/authStore';

function isSubscriptionActive(
  status: string | undefined,
  expiresAt: string | null | undefined,
): boolean {
  if (status !== 'active' || !expiresAt) return false;
  return new Date(expiresAt).getTime() > Date.now();
}

function hasUnlimitedRole(perms: string[] | undefined, slug: string | undefined): boolean {
  if (slug === 'subscriber') return true;
  return (perms ?? []).includes('billing.unlimited');
}

export function TokensBadge() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  const sub =
    isSubscriptionActive(user.subscription_status, user.subscription_expires_at) ||
    hasUnlimitedRole(user.role?.permissions, user.role?.slug);
  const balance = user.tokens_balance ?? 0;
  const low = !sub && balance <= 4;

  // Color = green when comfortable, amber when low, accent for subscribers.
  const fg = sub
    ? 'var(--accent)'
    : low
      ? '#f59e0b'
      : 'var(--text-primary)';
  const bg = sub ? 'rgba(245,158,11,0.12)' : 'var(--surface-2)';

  return (
    <Link
      to="/app/billing"
      className="flex h-9 items-center gap-1.5 rounded-[var(--radius-pill)] border px-3 text-xs no-underline transition-colors hover:opacity-90"
      style={{ background: bg, borderColor: 'var(--border-default)', color: fg }}
      title={
        sub
          ? `Suscripción activa hasta ${user.subscription_expires_at?.slice(0, 10) ?? ''}`
          : low
            ? 'Saldo bajo — recarga fierros para seguir exportando'
            : 'Balance de fierros'
      }
    >
      {sub ? (
        <>
          <InfinityIcon size={14} strokeWidth={2} aria-hidden />
          <span className="font-mono tabular">ilimitado</span>
        </>
      ) : (
        <>
          <FierroIcon size={14} />
          <span className="font-mono tabular">{balance}</span>
        </>
      )}
    </Link>
  );
}
